import './styles/EventsList.scss';

import { useApolloClient } from '@apollo/client';
import { CloudDownload, Create } from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  Icon,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Tab,
  Tabs
} from '@mui/material';

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import USER_QUERY from 'gql/queries/currentUser.gql';

import ContextMenu from 'app/components/ContextMenu';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { Modal } from 'app/components/Modal';
import { translations } from 'locales/i18n';
import { ACLHelper } from 'utils/ACLHelper';
import usePageHeading from 'utils/hooks/usePageHeading';

import { IOEvent } from '../IOEvent';
import { IOEventsList } from './components/IOEventsList';

export function EventsList() {
  const { t } = useTranslation();
  const client = useApolloClient();
  const anchorRef = useRef(null);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [reportMenuOpen, setReportMenuOpen] = useState(false);

  const [activeTab, setActiveTab] = useState(0);

  const { currentUser } = client.readQuery({ query: USER_QUERY });

  const aclHelper = new ACLHelper(currentUser);

  usePageHeading({
    heading: t(translations.pages.internationalOffice.eventsTitleLong()),
    mobilePadding: false,
    className: 'events-management'
  });

  const ReportMenu = () => {
    return (
      <ContextMenu
        open={reportMenuOpen}
        setOpen={setReportMenuOpen}
        anchorEl={anchorRef.current}
        className="courses-management__contextmenu"
      >
        <MenuItem>
          <Grid
            container
            className="courses-management__contextmenu__container"
            onClick={() => {
              window.open(`${import.meta.env.VITE_GATEWAY_URL}/reports/events`, '_blank');
            }}
          >
            <Grid item xs={3}>
              <ListItemIcon>
                <Icon
                  className="fas fa-calendar-check"
                  style={{
                    width: 'auto'
                  }}
                />
              </ListItemIcon>
            </Grid>
            <Grid item xs={9}>
              <ListItemText
                primary={t(translations.pages.internationalOffice.events.eventsReport())}
              />
            </Grid>
          </Grid>
        </MenuItem>
        <MenuItem>
          <Grid
            container
            className="courses-management__contextmenu__container"
            onClick={() => {
              window.open(`${import.meta.env.VITE_GATEWAY_URL}/reports/officers/events`, '_blank');
            }}
          >
            <Grid item xs={3}>
              <ListItemIcon>
                <Icon
                  className="fas fa-user-clock"
                  style={{
                    width: 'auto'
                  }}
                />
              </ListItemIcon>
            </Grid>
            <Grid item xs={9}>
              <ListItemText
                primary={t(translations.pages.internationalOffice.events.organisersReport())}
              />
            </Grid>
          </Grid>
        </MenuItem>
      </ContextMenu>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return <IOEventsList />;
      case 1:
        return <IOEventsList showOnlyEnrolled />;
      default:
        return null;
    }
  };

  return aclHelper.isIncomingsAdmin() ? (
    <>
      <ErrorBoundary boundaryTag="ioEventMangegementBoundary">
        <Modal
          open={createModalOpen}
          onClose={(event, reason) => {
            if (reason && reason === 'backdropClick') return;
            setCreateModalOpen(false);
          }}
          headline={t(translations.pages.internationalOffice.events.newIOEvent())}
          onCancel={() => setCreateModalOpen(false)}
          disabledCancel={disabledCancel}
        >
          <IOEvent
            closeHandler={() => {
              setCreateModalOpen(false);
            }}
            setDisabledCancel={setDisabledCancel}
          />
        </Modal>
      </ErrorBoundary>
      <Grid
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          paddingBottom: '1em'
        }}
        container
      >
        <Button
          startIcon={<CloudDownload />}
          variant="outlined"
          color="primary"
          className="courses-management__actions--secondary"
          onClick={() => {
            setReportMenuOpen(true);
          }}
          ref={anchorRef}
        >
          {t(translations.pages.internationalOffice.courses.reports())}
        </Button>

        <Button
          startIcon={<Create />}
          variant="contained"
          className="users-management__actions--primary"
          onClick={() => {
            setCreateModalOpen(true);
          }}
        >
          {t(translations.common.create())}
        </Button>
        <ReportMenu />
      </Grid>
      <Box className="events-management__paper" component={Paper}>
        <IOEventsList />
      </Box>
    </>
  ) : (
    <Box className="events-management__paper" component={Paper}>
      <Tabs
        indicatorColor="primary"
        value={activeTab}
        onChange={(e, newVal) => setActiveTab(newVal)}
        className="invoice-management__tabs"
      >
        <Tab label={t(translations.pages.internationalOffice.eventsTitle())} />
        <Tab label={t(translations.pages.internationalOffice.myEvents())} />
      </Tabs>
      {renderTabContent()}
    </Box>
  );
}
